/**
 * Shared UI component for UI Button.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

import { UiButtonSize, UiButtonType, UiButtonVariant } from './ui-button.types';
import { resolveTranslatedUiLabel } from '../ui-label-translate.util';

@Component({
  selector: 'ui-button',
  standalone: true,
  templateUrl: './ui-button.component.html',
  styleUrls: ['./ui-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiButtonComponent implements AfterViewChecked {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly variant = input<UiButtonVariant>('primary');
  readonly size = input<UiButtonSize>('md');
  readonly type = input<UiButtonType>('button');
  readonly disabled = input(false);
  readonly fullWidth = input(false);
  readonly ariaLabel = input('');
  readonly badgeText = input('');
  readonly icon = input('');
  readonly iconPosition = input<'start' | 'end'>('start');
  readonly label = input('');

  /** Bumped on every language change so the translated label recomputes. */
  private readonly langVersion = signal(0);
  /** Common raw projected labels that the wrapper can safely own and translate. */
  private readonly projectedSource = signal('');

  /**
   * Reactively resolved label. Recomputes when the source text changes or the
   * active language changes — no DOM mutation, so OnPush + content projection
   * both update live on language switch.
   */
  readonly translatedLabel = computed(() => {
    this.langVersion();
    const source = this.label().trim();
    return source ? resolveTranslatedUiLabel(this.translate, source) : '';
  });

  readonly translatedProjectedLabel = computed(() => {
    this.langVersion();
    const source = this.projectedSource();
    return source ? resolveTranslatedUiLabel(this.translate, source) : '';
  });

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.langVersion.update((version) => version + 1));
  }

  ngAfterViewChecked(): void {
    if (this.label().trim()) {
      return;
    }

    const label = this.host.nativeElement.querySelector('.ui-button__label');
    const projected = label?.textContent?.trim() ?? '';

    if (
      projected
      && label?.childElementCount === 0
      && projected !== this.projectedSource()
      && projected !== this.translatedProjectedLabel()
      && this.isSupportedRawProjection(projected)
    ) {
      this.projectedSource.set(projected);
      this.cdr.markForCheck();
    }
  }

  private isSupportedRawProjection(value: string): boolean {
    return [
      'apply filters',
      'back',
      'cancel',
      'close',
      'create',
      'delete',
      'export',
      'info',
      'next',
      'previous',
      'reset',
      'retry',
      'save',
      'success',
      'update'
    ].includes(value.trim().toLowerCase());
  }
}
